
import { supabase } from "@/integrations/supabase/client";
import { ModalCampaign, ModalCampaignFormData, ModalCampaignUpdateData } from "@/types/modalCampaigns";

/**
 * Fetch all campaigns for the admin interface
 */
export const fetchAllCampaigns = async (): Promise<ModalCampaign[]> => {
  const { data, error } = await supabase
    .from('modal_campaigns')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all campaigns:', error);
    throw error;
  }

  return data;
};

/**
 * Create a new campaign
 */
export const createCampaign = async (
  campaign: ModalCampaignFormData, 
  createdById: string
): Promise<string> => {
  // First create the campaign
  const { data: campaignData, error: campaignError } = await supabase
    .from('modal_campaigns')
    .insert({
      title: campaign.title,
      content: campaign.content,
      start_date: campaign.start_date.toISOString(),
      end_date: campaign.end_date.toISOString(),
      frequency: campaign.frequency,
      created_by: createdById,
      has_quiz: campaign.has_quiz,
      min_score: campaign.min_score,
      max_attempts: campaign.max_attempts,
      repeat_on_failure: campaign.repeat_on_failure,
      allow_dismiss: campaign.allow_dismiss
    })
    .select()
    .single();

  if (campaignError) {
    console.error('Error creating campaign:', campaignError);
    throw campaignError;
  }

  const campaignId = campaignData.id;

  // Then assign users to the campaign
  if (campaign.users && campaign.users.length > 0) {
    const userEntries = campaign.users.map(userId => ({
      campaign_id: campaignId,
      user_id: userId
    }));

    const { error: usersError } = await supabase
      .from('modal_campaign_users')
      .insert(userEntries);

    if (usersError) {
      console.error('Error assigning users to campaign:', usersError);
      throw usersError;
    }
  }

  // Finally, add questions if this is a quiz
  if (campaign.has_quiz && campaign.questions && campaign.questions.length > 0) {
    const questionEntries = campaign.questions.map((q, index) => ({
      campaign_id: campaignId,
      question_text: q.question_text,
      options: q.options,
      correct_options: q.correct_options,
      points: q.points,
      order_number: q.order_number || index
    }));

    const { error: questionsError } = await supabase
      .from('modal_campaign_questions')
      .insert(questionEntries);

    if (questionsError) {
      console.error('Error adding questions to campaign:', questionsError);
      throw questionsError;
    }
  }

  return campaignId;
};

/**
 * Update an existing campaign
 */
export const updateCampaign = async (
  id: string,
  campaign: ModalCampaignUpdateData
): Promise<void> => {
  const updateData: any = { ...campaign };
  
  // Convert dates to ISO strings
  if (updateData.start_date) {
    updateData.start_date = updateData.start_date.toISOString();
  }
  if (updateData.end_date) {
    updateData.end_date = updateData.end_date.toISOString();
  }

  // Remove fields that shouldn't be directly updated
  delete updateData.users;
  delete updateData.questions;

  const { error } = await supabase
    .from('modal_campaigns')
    .update(updateData)
    .eq('id', id);

  if (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }

  // Handle updating users if provided
  if (campaign.users) {
    // First delete existing user associations
    const { error: deleteError } = await supabase
      .from('modal_campaign_users')
      .delete()
      .eq('campaign_id', id);

    if (deleteError) {
      console.error('Error deleting existing users:', deleteError);
      throw deleteError;
    }

    // Then add new user associations
    if (campaign.users.length > 0) {
      const userEntries = campaign.users.map(userId => ({
        campaign_id: id,
        user_id: userId
      }));

      const { error: insertError } = await supabase
        .from('modal_campaign_users')
        .insert(userEntries);

      if (insertError) {
        console.error('Error reassigning users:', insertError);
        throw insertError;
      }
    }
  }

  // Handle updating questions if provided and if quiz is enabled
  if (campaign.has_quiz && campaign.questions) {
    // First delete existing questions
    const { error: deleteError } = await supabase
      .from('modal_campaign_questions')
      .delete()
      .eq('campaign_id', id);

    if (deleteError) {
      console.error('Error deleting existing questions:', deleteError);
      throw deleteError;
    }

    // Then add new questions
    if (campaign.questions.length > 0) {
      const questionEntries = campaign.questions.map((q, index) => ({
        campaign_id: id,
        question_text: q.question_text,
        options: q.options,
        correct_options: q.correct_options,
        points: q.points,
        order_number: q.order_number || index
      }));

      const { error: insertError } = await supabase
        .from('modal_campaign_questions')
        .insert(questionEntries);

      if (insertError) {
        console.error('Error adding updated questions:', insertError);
        throw insertError;
      }
    }
  }
};

/**
 * Delete a campaign and its associated data
 */
export const deleteCampaign = async (id: string): Promise<void> => {
  // First delete related records in child tables
  try {
    // Delete questions
    await supabase
      .from('modal_campaign_questions')
      .delete()
      .eq('campaign_id', id);
      
    // Delete user associations
    await supabase
      .from('modal_campaign_users')
      .delete()
      .eq('campaign_id', id);
    
    // Delete logs and quiz results (these might have foreign key constraints)
    const { data: logs } = await supabase
      .from('modal_campaign_user_logs')
      .select('id')
      .eq('campaign_id', id);
    
    if (logs && logs.length > 0) {
      // Delete quiz results related to these logs
      for (const log of logs) {
        await supabase
          .from('modal_campaign_user_quiz_results')
          .delete()
          .eq('log_id', log.id);
      }
      
      // Delete the logs
      await supabase
        .from('modal_campaign_user_logs')
        .delete()
        .eq('campaign_id', id);
    }
    
    // Delete quiz results directly related to campaign
    await supabase
      .from('modal_campaign_user_quiz_results')
      .delete()
      .eq('campaign_id', id);
    
    // Finally delete the campaign itself
    const { error } = await supabase
      .from('modal_campaigns')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

/**
 * Export campaign statistics data
 */
export const exportCampaignData = async (campaignId: string): Promise<Blob> => {
  // Fetch the campaign data
  const { data: campaign, error: campaignError } = await supabase
    .from('modal_campaigns')
    .select('*')
    .eq('id', campaignId)
    .single();

  if (campaignError) {
    throw new Error('Kan campagne niet vinden');
  }

  // Fetch the view logs
  const { data: viewLogs, error: viewError } = await supabase
    .from('modal_campaign_user_logs')
    .select('*')
    .eq('campaign_id', campaignId);

  if (viewError) {
    throw new Error('Fout bij ophalen van weergavegegevens');
  }

  // Fetch the quiz results if it's a quiz campaign
  let quizResults = [];
  if (campaign.has_quiz) {
    const { data, error } = await supabase
      .from('modal_campaign_user_quiz_results')
      .select('*')
      .eq('campaign_id', campaignId);

    if (!error && data) {
      quizResults = data;
    }
  }

  // Generate CSV content
  const createCsvContent = () => {
    let csv = `Campagne Statistieken: ${campaign.title}\n`;
    csv += `Start datum: ${campaign.start_date}\n`;
    csv += `Eind datum: ${campaign.end_date}\n\n`;
    
    csv += 'WEERGAVEGEGEVENS\n';
    csv += 'Gebruiker ID,Datum bekeken,Gesloten,Permanent gesloten\n';
    
    viewLogs.forEach((log: any) => {
      csv += `${log.user_id},${log.displayed_at},${log.dismissed_at || ''},${log.dismissed_permanently ? 'Ja' : 'Nee'}\n`;
    });
    
    if (campaign.has_quiz) {
      csv += '\nQUIZ RESULTATEN\n';
      csv += 'Gebruiker ID,Poging,Score,Geslaagd,Datum voltooid\n';
      
      quizResults.forEach((result: any) => {
        csv += `${result.user_id},${result.attempt_number},${result.score},${result.passed ? 'Ja' : 'Nee'},${result.completed_at}\n`;
      });
    }
    
    return csv;
  };

  const csvContent = createCsvContent();
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  return blob;
};
